import * as THREE from 'three';
import { DEFAULT_DOJO_CAMERA } from './config';

export const createCameraController = ({ camera, sparkRenderer, onSparkDirty }) => {
    const fixedCameraPosition = new THREE.Vector3(
        DEFAULT_DOJO_CAMERA.position.x,
        DEFAULT_DOJO_CAMERA.position.y,
        DEFAULT_DOJO_CAMERA.position.z
    );
    const fixedCameraBaseQuaternion = new THREE.Quaternion(
        DEFAULT_DOJO_CAMERA.quaternion.x,
        DEFAULT_DOJO_CAMERA.quaternion.y,
        DEFAULT_DOJO_CAMERA.quaternion.z,
        DEFAULT_DOJO_CAMERA.quaternion.w
    );

    const cameraYawAxis        = new THREE.Vector3(0, 1, 0);
    const pitchQuaternion      = new THREE.Quaternion();
    const pitchLimit           = THREE.MathUtils.degToRad(85);
    const horizontalRotateSpeed = 0.005;
    const verticalRotateSpeed   = 0.004;

    let accumulatedYaw   = 0;
    let accumulatedPitch = 0;

    const applyFixedCameraPose = () => {
        const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(cameraYawAxis, accumulatedYaw);
        const cameraRightAxis = new THREE.Vector3(1, 0, 0)
            .applyQuaternion(yawQuaternion)
            .applyQuaternion(fixedCameraBaseQuaternion)
            .normalize();
        pitchQuaternion.setFromAxisAngle(cameraRightAxis, accumulatedPitch);

        camera.position.copy(fixedCameraPosition);
        camera.quaternion.copy(pitchQuaternion.multiply(yawQuaternion).multiply(fixedCameraBaseQuaternion));
        onSparkDirty();
    };

    const applyMouseRotationDelta = (deltaX, deltaY) => {
        accumulatedYaw   -= deltaX * horizontalRotateSpeed;
        accumulatedPitch -= deltaY * verticalRotateSpeed;
        accumulatedPitch  = THREE.MathUtils.clamp(accumulatedPitch, -pitchLimit, pitchLimit);
        if (Math.abs(accumulatedYaw) > Math.PI * 2) accumulatedYaw %= Math.PI * 2;
        applyFixedCameraPose();
        sparkRenderer.setDirty();
    };

    const getCurrentCameraTarget = () => {
        const forward  = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        const distance = DEFAULT_DOJO_CAMERA.pivotDistance ?? 1.8;
        return {
            x: camera.position.x + forward.x * distance,
            y: camera.position.y + forward.y * distance,
            z: camera.position.z + forward.z * distance,
        };
    };

    const registerWindowExports = () => {
        window.getDojoCameraParams = () => ({
            position:      { x: camera.position.x, y: camera.position.y, z: camera.position.z },
            target:        getCurrentCameraTarget(),
            quaternion:    { x: camera.quaternion.x, y: camera.quaternion.y, z: camera.quaternion.z, w: camera.quaternion.w },
            pivotDistance: DEFAULT_DOJO_CAMERA.pivotDistance ?? 1.8,
        });
        window.getDojoCameraPose = window.getDojoCameraParams;
        window.copyDojoCameraParams = async () => {
            const params = window.getDojoCameraParams();
            const text = JSON.stringify(params, null, 2);
            try { if (navigator.clipboard) await navigator.clipboard.writeText(text); }
            catch (err) { console.warn('Clipboard write failed:', err); }
            console.log('[Dojo] camera params:', params);
            return params;
        };
    };

    const unregisterWindowExports = () => {
        delete window.getDojoCameraParams;
        delete window.getDojoCameraPose;
        delete window.copyDojoCameraParams;
    };

    return {
        applyFixedCameraPose,
        applyMouseRotationDelta,
        registerWindowExports,
        unregisterWindowExports,
    };
};
